<template>
  <div class="spl-export-proof-edit-columns">
    <div class="spl-export-proof-edit-columns__container" style="grid-column: 1">
      <h3>Selected Columns *</h3>
      <p v-if="selectedColumns.length === 0" class="spl-export-proof-edit-columns__error">
        Please select at least one Column
      </p>
      <p v-if="!weight" class="spl-export-proof-edit-columns__error">
        Weight should be included as Column
      </p>
      <draggable
        v-model="selectedColumns"
        :options="dragOptionsSelected"
        class="list-group">
        <div
          v-for="element in selectedColumns"
          :key="element.Name"
          class="list-group-item">
          <p>{{ element.Default_Label }}</p>
          <form-label>Custom Label</form-label>
          <form-input :value="element.Label" @input="updateCustomLabel($event, element)" />
          <div v-if="element.Function_List.length > 1">
            <form-label>Function</form-label>
            <form-select
              :id="element.Name"
              :options="element.Function_List.map((element)=>{return {label: element, value: element}})"
              :value="{value: element.Function}"
              @valueSelected="updateCustomFunction($event, element)" />
          </div>
        </div>
      </draggable>
    </div>

    <div class="spl-export-proof-edit-columns__container" style="grid-column: 2">
      <h3>Available Columns</h3>
      <form-input v-model="filter" placeholder="Filter" />
      <draggable
        v-model="availableColumns"
        :options="dragOptionsAvailable"
        class="list-group list-group--smaller"
        @change="handleChangeAvailable"
        @start="isDragging=true"
        @end="isDragging=false">
        <transition-group
          class="list-group-item-empty"
          name="no">
          <div
            v-for="(element, index) in availableColumns"
            :key="element.Name"
            :data-cy="'edit-export-proof-column-' + kebabCase(element.Name)"
            @click.shift="addColumn(element)">
            <h3 v-if="(!availableColumns[index - 1] || availableColumns[index-1].Group != element.Group)" :key="element.Name + '-group'">
              {{ element.Group }}
            </h3>
            <div class="list-group-item">
              {{ element.Default_Label }}
            </div>
          </div>
        </transition-group>
      </draggable>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { filter, find, sortBy, set, cloneDeep, kebabCase } from 'lodash'
import { mapState, mapMutations } from 'vuex'

export default {
  components: {
    draggable
  },
  data () {
    return {
      availableColumns: [],
      selectedColumns: [],
      filter: '',
      isDragging: false,
      kebabCase,
      weight: false
    }
  },
  computed: {
    ...mapState('exportProof', ['currentEditExportProof', 'exportProofColumns']),
    dragOptionsSelected () {
      // Options for the Vue.Draggable elment with the selected columns
      return {
        animation: 0,
        disabled: false,
        group: 'description',
        ghostClass: 'ghost'
      }
    },
    dragOptionsAvailable () {
      // Options for the Vue.Draggable elment with the avialable options
      return {
        animation: 0,
        disabled: false,
        sort: false,
        group: 'description',
        ghostClass: 'ghost'
      }
    }
  },
  watch: {
    selectedColumns: {
      handler: function () {
        this.weight = this.selectedColumns.some(item => item.Name === 'Weight')
        // Updates the vuex model everytime the columns change
        this.setCurrentEditExportProof(set(cloneDeep(this.currentEditExportProof), 'Export_Proof_Layout', this.selectedColumns))
      },
      deep: true
    },
    filter () {
      this.updateAvailableColumns()
    }
  },
  mounted () {
    if (this.currentEditExportProof.Export_Proof_Layout.length === 0) {
      this.selectedColumns = this.exportProofColumns
    } else {
      this.selectedColumns = this.currentEditExportProof.Export_Proof_Layout
    }
    this.updateAvailableColumns()
  },
  methods: {
    ...mapMutations('exportProof', ['setCurrentEditExportProof']),
    handleChangeAvailable () {
      this.updateAvailableColumns()
    },
    addColumn (column) {
      this.selectedColumns.push(column)
      this.updateAvailableColumns()
    },
    // Everytime we change something (filter, or columns)
    // we filter the available side so it does not show items already selected
    // After that it sorts the columns on the Group field so the groups stay together
    updateAvailableColumns () {
      this.availableColumns = sortBy(filter(this.exportProofColumns, (column) => {
        if (find(this.selectedColumns, { Name: column.Name })) {
          return false
        }
        if (this.filter === '') {
          return true
        }
        var label = column.Label.toLowerCase() || ''
        return label.indexOf(this.filter.toLowerCase()) > -1
      }), ['Group'])
    },
    updateCustomFunction (value, element) {
      // Function selected for selected field
      element.Function = value.value
    },
    updateCustomLabel (value, element) {
      element.Label = value
    }
  }
}
</script>

<style lang="scss">
  @import '../../../variables.scss';

  .spl-export-proof-edit-columns {
    display: grid;
    grid-template-columns: 48% 48%;
    width: 100%;
    &__container {
      margin: 10px;
    }
    .flip-list-move {
      transition: transform 0.5s;
    }

    .no-move {
      transition: transform 0s;
    }

    .ghost {
      opacity: .5;
      background: #C8EBFB;
    }
    .ghost h3 {
        display: none !important;
    }
    .list-group {
      background-color: $grey_95;
      padding: 15px;
      overflow-y: auto;
      height: calc(100vh - 300px);
      border-radius: 5px;
      min-height: 50px;
      min-width: 50px;
      & select {
        margin-top: 10px;
      }
      &--smaller {
        height: calc(100vh - 353px);
        min-height: 50px;
        min-width: 50px;
      }
    }
    &__error {
      color: $errorRed;
    }

    .list-group-item {
      cursor: move;
      padding: 6px;
      background-color: $grey_90;
      border: 1px solid $grey_80;
      border-radius: 4px;
      margin-bottom: 8px;
      user-select: none;
      p {
        margin: 1px 0px 12px 0;
      }
      label {
        margin-bottom: 4px;
        margin-top: 5px;
        margin-left: 1px;
      }
      select {
        margin-top: 0;
      }
      .spl-input {
        margin-bottom: 5px;
      }
    }

    .list-group-item-empty {
      cursor: move;
      padding-top: 10px;
      padding-right: 95%;
      padding-bottom: 10%;
      border-radius: 4px;
      margin-bottom: 8px;
      user-select: none;
      p {
        margin: 1px 0px 12px 0;
      }
      label {
        margin-bottom: 4px;
        margin-top: 5px;
        margin-left: 1px;
      }
      select {
        margin-top: 0;
      }
      .spl-input {
        margin-bottom: 5px;
      }
    }
  }

</style>
